import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, KeyRound, Lock, ArrowLeft, CheckCircle2 } from "lucide-react";

const ForgotPassword = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password, 4=done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If this email is registered, an OTP has been sent.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      setResetToken(data.reset_token);
      toast.success("OTP verified — set your new password.");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, reset_token: resetToken, new_password: pw });
      toast.success("Password updated!");
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-8" data-testid="fp-back"><ArrowLeft size={14}/>Back to sign in</Link>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>Forgot password</h1>
        <p className="text-slate-500 mb-8">We'll send a 6-digit OTP to your registered email.</p>

        <div className="qw-card p-6 md:p-8">
          {step === 1 && (
            <form onSubmit={requestOtp} className="space-y-4" data-testid="fp-step-email">
              <div>
                <label className="qw-label">Registered Email</label>
                <div className="relative"><Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@quickwash.in" className="qw-input pl-10" data-testid="fp-email"/>
                </div>
              </div>
              <button disabled={loading} className="qw-btn-primary w-full" data-testid="fp-send-otp">{loading ? "Sending…" : "Send OTP"}</button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="fp-step-otp">
              <div className="text-sm text-slate-500 mb-2">OTP sent to <span className="font-semibold text-slate-900">{email}</span></div>
              <div>
                <label className="qw-label">Enter 6-digit OTP</label>
                <div className="relative"><KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="text" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} required className="qw-input pl-10 tracking-[0.4em] text-center text-lg font-semibold" data-testid="fp-otp"/>
                </div>
              </div>
              <button disabled={loading} className="qw-btn-primary w-full" data-testid="fp-verify-otp">{loading ? "Verifying…" : "Verify OTP"}</button>
              <button type="button" onClick={() => setStep(1)} className="qw-btn-ghost w-full text-sm">Change email</button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={resetPw} className="space-y-4" data-testid="fp-step-reset">
              <div>
                <label className="qw-label">New Password</label>
                <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} placeholder="At least 6 characters" className="qw-input pl-10" data-testid="fp-newpw"/>
                </div>
              </div>
              <div>
                <label className="qw-label">Confirm Password</label>
                <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={6} className="qw-input pl-10" data-testid="fp-newpw2"/>
                </div>
              </div>
              <button disabled={loading} className="qw-btn-primary w-full" data-testid="fp-reset-submit">{loading ? "Updating…" : "Set New Password"}</button>
            </form>
          )}

          {step === 4 && (
            <div className="text-center py-4" data-testid="fp-done">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3"><CheckCircle2 size={28}/></div>
              <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>Password updated</div>
              <p className="text-sm text-slate-500 mt-1 mb-5">You can now sign in with your new password.</p>
              <button onClick={() => nav("/login")} className="qw-btn-primary" data-testid="fp-go-login">Go to Sign in</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
